import { reactive } from 'vue'
import { getCurrentMenuPolicy, getCurrentMenuTree, type MenuPolicy, type SysMenu } from '../api/menu'

interface MenuState {
  tree: SysMenu[]
  loaded: boolean
  available: boolean
  knownPaths: Set<string>
  visiblePaths: Set<string>
  blockedPaths: Set<string>
  titleByPath: Record<string, string>
  sortByPath: Record<string, number>
}

export const menuState = reactive<MenuState>({
  tree: [],
  loaded: false,
  available: false,
  knownPaths: new Set<string>(),
  visiblePaths: new Set<string>(),
  blockedPaths: new Set<string>(),
  titleByPath: {},
  sortByPath: {},
})

const normalizePath = (path: string | null | undefined) => {
  if (!path) return ''
  const trimmed = path.trim()
  return trimmed.startsWith('/') ? trimmed : `/${trimmed}`
}

const applyPolicy = (policy: MenuPolicy | null | undefined) => {
  menuState.knownPaths = new Set((policy?.known_paths || []).map(normalizePath).filter(Boolean))
  menuState.visiblePaths = new Set((policy?.visible_paths || []).map(normalizePath).filter(Boolean))
  menuState.blockedPaths = new Set((policy?.blocked_paths || []).map(normalizePath).filter(Boolean))
  menuState.titleByPath = policy?.title_by_path || {}
  menuState.sortByPath = policy?.sort_by_path || {}
}

export const loadConfiguredMenus = async () => {
  try {
    const [treeRes, policyRes] = await Promise.all([
      getCurrentMenuTree(),
      getCurrentMenuPolicy(),
    ])
    menuState.tree = Array.isArray(treeRes.data) ? treeRes.data : []
    applyPolicy(policyRes.data)
    menuState.loaded = true
    menuState.available = menuState.knownPaths.size > 0 || menuState.tree.length > 0
  } catch (error) {
    menuState.tree = []
    applyPolicy(null)
    menuState.loaded = true
    menuState.available = false
  }
}

export const clearConfiguredMenus = () => {
  menuState.tree = []
  menuState.loaded = false
  menuState.available = false
  applyPolicy(null)
}

export const isConfiguredMenuVisible = (path: string) => {
  const normalized = normalizePath(path)
  if (!normalized || !menuState.available || menuState.knownPaths.size === 0) {
    return true
  }
  if (!menuState.knownPaths.has(normalized)) {
    return true
  }
  return menuState.visiblePaths.has(normalized)
}

export const isConfiguredMenuPathAllowed = (path: string) => {
  const normalized = normalizePath(path)
  if (!normalized || !menuState.available || menuState.knownPaths.size === 0) {
    return true
  }
  if (menuState.blockedPaths.has(normalized)) {
    return false
  }
  for (const blocked of menuState.blockedPaths) {
    if (normalized.startsWith(`${blocked}/`)) {
      return false
    }
  }
  return true
}

export const getConfiguredMenuTitle = (path: string) => {
  const normalized = normalizePath(path)
  return menuState.titleByPath[normalized] || ''
}

export const getConfiguredMenuSort = (path: string) => {
  const normalized = normalizePath(path)
  return menuState.sortByPath[normalized] ?? 0
}
